import { z } from 'zod';
import { toast } from 'sonner';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, CircleAlert } from 'lucide-react';
import { useForm, Controller, useFormContext } from 'react-hook-form';

import { Input } from '@shared/components/ui/input';
import { Alert } from '@shared/components/ui/alert';
import { Button } from '@shared/components/ui/button';
import { Form, Field } from '@shared/components/hook-form';
import { FieldShell } from '@shared/components/hook-form/field-shell';

import { useToken } from '@features/user-access';
import { useCurrentIp } from '@features/auth/slices/ip-slice';
import { useLoginMutation } from '@features/auth/api/auth-api';

const loginSchema = z.object({
  username: z.string().min(1, "Le nom d'utilisateur est requis"),
  password: z.string().min(1, 'Le mot de passe est requis'),
  ip: z.string(),
});

type LoginFormData = z.infer<typeof loginSchema>;

/** Password input with a show/hide toggle (useful on phones, where typos are frequent). */
const PasswordField = () => {
  const { control } = useFormContext<LoginFormData>();
  const [visible, setVisible] = useState(false);

  return (
    <Controller
      control={control}
      name="password"
      render={({ field, fieldState: { error } }) => (
        <FieldShell error={error?.message} id="login-password" label="Mot de passe">
          <div className="relative">
            <Input
              {...field}
              aria-invalid={error ? true : undefined}
              autoComplete="current-password"
              className="pr-12"
              id="login-password"
              type={visible ? 'text' : 'password'}
            />
            <Button
              aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              aria-pressed={visible}
              className="absolute top-1/2 right-1 -translate-y-1/2"
              onClick={() => setVisible((value) => !value)}
              size="icon"
              type="button"
              variant="ghost"
            >
              {visible ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
            </Button>
          </div>
        </FieldShell>
      )}
    />
  );
};

export const LoginForm = () => {
  const ip = useCurrentIp().toString();
  const [login, { isLoading }] = useLoginMutation();
  const { setToken } = useToken();
  const navigate = useNavigate();

  const methods = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      ip,
    },
  });

  const [loginError, setLoginError] = useState(false);
  const { handleSubmit } = methods;

  const onSubmit = handleSubmit(async (data: LoginFormData) => {
    setLoginError(false);
    try {
      const response = await login(data).unwrap();
      if (response.token) {
        setToken(response.token);
        toast.success('Connexion réussie');
        navigate('/');
      }
    } catch {
      setLoginError(true);
    }
  });

  return (
    <Form className="flex flex-col gap-5" methods={methods} onSubmit={onSubmit}>
      {loginError && (
        <Alert onClose={() => setLoginError(false)} variant="destructive">
          <CircleAlert />
          <div className="col-start-2">Nom d'utilisateur ou mot de passe incorrect.</div>
        </Alert>
      )}
      <Field.Text
        autoCapitalize="none"
        autoComplete="username"
        autoCorrect="off"
        autoFocus
        label="Nom d'utilisateur"
        name="username"
        spellCheck={false}
      />
      <PasswordField />
      <Button className="mt-2 w-full" loading={isLoading} size="lg" type="submit">
        Se connecter
      </Button>
    </Form>
  );
};
